import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { BreadcrumbJsonLd, ItemListJsonLd } from "../components/JsonLd";
import { Flame, Eye, Heart, ArrowLeft, ArrowRight, Trophy, Play } from "lucide-react";

export default function Trending() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/videos/trending?limit=20")
      .then(r => setVideos(r.data.videos || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12" data-testid="trending-page">
      <BreadcrumbJsonLd items={[{ name: "Accueil", path: "/" }, { name: "Tendances", path: "/trending" }]} />
      <ItemListJsonLd
        name="Top 20 tendances · Fedevo 2.0"
        description="Les vidéos les plus vues et les plus aimées toutes chaînes confondues"
        items={videos.map(v => ({ name: v.title, path: `/watch/${v.id}`, image: v.thumbnail }))}
      />

      <div className="mb-10">
        <Link to="/" className="inline-block mb-4 text-xs uppercase tracking-[0.3em] text-[color:var(--text-muted)] hover:text-[color:var(--accent)]">
          <ArrowLeft className="w-3 h-3 inline mr-1" /> Retour
        </Link>
        <div className="flex items-center gap-3 mb-3">
          <Flame className="w-7 h-7 text-[color:var(--accent)]" />
          <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--accent)]">Algorithme · views + likes ×5</div>
        </div>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-none">Tendances · Top 20</h1>
          <Link
            to="/battle"
            data-testid="link-battle"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-yellow-400 via-red-500 to-pink-500 text-black text-xs uppercase tracking-widest font-semibold shadow-lg hover:brightness-110 transition"
          >
            <Trophy className="w-4 h-4" /> Mode Battle
          </Link>
        </div>
        <p className="text-[color:var(--text-muted)] mt-4 max-w-2xl">
          Le palmarès des vidéos qui font vibrer la communauté Fedevo 2.0 — toutes chaînes confondues.
        </p>
      </div>

      {loading && (
        <div className="py-20 text-center text-[color:var(--text-muted)]">Chargement du classement...</div>
      )}

      {!loading && videos.length === 0 && (
        <div className="py-20 text-center text-[color:var(--text-muted)]" data-testid="trending-empty">
          Pas encore assez de vues pour établir un classement. Revenez bientôt !
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 stagger" data-testid="trending-grid">
        {videos.map((v, i) => (
          <Link
            to={`/watch/${v.id}`}
            key={v.id}
            data-testid={`trending-card-${i + 1}`}
            className="group card-hover rounded-xl overflow-hidden border border-[color:var(--border)] bg-[color:var(--bg-elev)] block relative"
          >
            <div className="relative aspect-video bg-black overflow-hidden">
              {v.thumbnail && (
                <img
                  src={v.thumbnail}
                  alt={v.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              )}
              <div
                className={`absolute top-2 left-2 w-9 h-9 rounded-full flex items-center justify-center font-display text-base shadow-lg ${
                  i === 0
                    ? "bg-gradient-to-br from-yellow-400 to-orange-500 text-black"
                    : i === 1
                    ? "bg-gradient-to-br from-gray-300 to-gray-500 text-black"
                    : i === 2
                    ? "bg-gradient-to-br from-amber-700 to-amber-500 text-white"
                    : "bg-black/70 backdrop-blur-md text-white border border-white/20"
                }`}
              >
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition" />
              <div className="absolute right-2 bottom-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-black/70 backdrop-blur-md text-[10px] uppercase tracking-widest text-[color:var(--accent)] font-semibold">
                <Flame className="w-3 h-3" /> {v.score}
              </div>
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[color:var(--accent)] text-black flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                <Play className="w-5 h-5 ml-0.5" />
              </div>
            </div>
            <div className="p-3">
              <div className="flex items-center gap-2 mb-1.5">
                {v.channel_color && (
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: v.channel_color }} />
                )}
                <span className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)] truncate">
                  {v.channel_name}
                </span>
              </div>
              <h3 className="text-sm font-medium leading-snug line-clamp-2 group-hover:text-[color:var(--accent)] transition mb-2">
                {v.title}
              </h3>
              <div className="flex items-center gap-3 text-[10px] text-[color:var(--text-muted)]">
                <span className="inline-flex items-center gap-1">
                  <Eye className="w-3 h-3" /> {v.views || 0}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Heart className="w-3 h-3" /> {v.likes || 0}
                </span>
                <ArrowRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
